/* Botifire UI kit — plain React + CSS tokens (styles/components.css). */
import {
  createContext, forwardRef, useCallback, useContext, useEffect, useId, useRef, useState,
  type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes,
} from 'react';
import { createPortal } from 'react-dom';
import { AlertCircle, AlertTriangle, CheckCircle2, Copy, Info, Loader2, Sparkles, X, Check } from 'lucide-react';
import { initials } from '@/lib/format';
import type { ApiError } from '@/lib/api';

const cx = (...c: (string | false | null | undefined)[]) => c.filter(Boolean).join(' ');
export { cx };

/* ───── Button ───── */
type BtnProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger' | 'ai'; size?: 'sm' | 'md' | 'lg';
  icon?: ReactNode; loading?: boolean; block?: boolean; iconOnly?: boolean;
};
export const Button = forwardRef<HTMLButtonElement, BtnProps>(function Button(
  { variant = 'secondary', size = 'md', icon, loading, block, iconOnly, className, children, disabled, type = 'button', ...rest }, ref) {
  return (
    <button ref={ref} type={type} disabled={disabled || loading} aria-busy={loading || undefined}
      className={cx('btn', `btn-${variant}`, size !== 'md' && `btn-${size}`, iconOnly && 'btn-icon', block && 'btn-block', className)} {...rest}>
      {loading ? <Loader2 className="spin" aria-hidden /> : icon}
      {!iconOnly && children}
      {iconOnly && <span className="sr-only">{children}</span>}
    </button>
  );
});

/* ───── Form fields ───── */
type FieldProps = { label?: ReactNode; hint?: ReactNode; error?: string | null; children: (id: string) => ReactNode; className?: string };
export function Field({ label, hint, error, children, className }: FieldProps) {
  const id = useId();
  return (
    <div className={cx('field', className)}>
      {label && <label className="field-label" htmlFor={id}>{label}</label>}
      {children(id)}
      {error ? <span className="field-error" role="alert">{error}</span> : hint ? <span className="field-hint">{hint}</span> : null}
    </div>
  );
}

type InputProps = InputHTMLAttributes<HTMLInputElement> & { label?: ReactNode; hint?: ReactNode; error?: string | null; icon?: ReactNode };
export const Input = forwardRef<HTMLInputElement, InputProps>(function Input({ label, hint, error, icon, className, ...rest }, ref) {
  return (
    <Field label={label} hint={hint} error={error} className={className}>
      {(id) => icon ? (
        <div className="input-group">{icon}<input ref={ref} id={rest.id ?? id} className="input" aria-invalid={!!error || undefined} {...rest} /></div>
      ) : <input ref={ref} id={rest.id ?? id} className="input" aria-invalid={!!error || undefined} {...rest} />}
    </Field>
  );
});

type TextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & { label?: ReactNode; hint?: ReactNode; error?: string | null };
export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea({ label, hint, error, className, ...rest }, ref) {
  return (
    <Field label={label} hint={hint} error={error} className={className}>
      {(id) => <textarea ref={ref} id={rest.id ?? id} className="textarea" aria-invalid={!!error || undefined} {...rest} />}
    </Field>
  );
});

type SelectProps = SelectHTMLAttributes<HTMLSelectElement> & {
  label?: ReactNode; hint?: ReactNode; error?: string | null;
  options: { value: string; label: string; disabled?: boolean }[]; placeholder?: string;
};
export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select({ label, hint, error, options, placeholder, className, ...rest }, ref) {
  return (
    <Field label={label} hint={hint} error={error} className={className}>
      {(id) => (
        <select ref={ref} id={rest.id ?? id} className="select" aria-invalid={!!error || undefined} {...rest}>
          {placeholder !== undefined && <option value="">{placeholder}</option>}
          {options.map((o) => <option key={o.value} value={o.value} disabled={o.disabled}>{o.label}</option>)}
        </select>
      )}
    </Field>
  );
});

export function Checkbox({ label, ...rest }: InputHTMLAttributes<HTMLInputElement> & { label: ReactNode }) {
  return <label className="check"><input type="checkbox" {...rest} />{label}</label>;
}

export function Switch({ checked, onChange, label, disabled }: { checked: boolean; onChange: (v: boolean) => void; label?: ReactNode; disabled?: boolean }) {
  return (
    <label className="switch">
      <input type="checkbox" role="switch" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} aria-checked={checked} />
      <span className="track" aria-hidden />
      {label && <span>{label}</span>}
    </label>
  );
}

/* ───── Layout pieces ───── */
/** The shell's top bar shows the current section title (the page's h1); pages repeat it only when they show
 *  something more specific, e.g. an agent's name. */
export const TopbarTitleContext = createContext<string | null>(null);

export function PageHeader({ title, description, actions, back }: { title: ReactNode; description?: ReactNode; actions?: ReactNode; back?: ReactNode }) {
  const topbar = useContext(TopbarTitleContext);
  const same = typeof title === 'string' && topbar !== null && title.trim().toLowerCase() === topbar.trim().toLowerCase();
  return (
    <header className={cx('page-header', same && 'compact')}>
      <div className="titles">{back}{!same && <h2 className="page-title">{title}</h2>}{description && <p>{description}</p>}</div>
      {actions && <div className="actions">{actions}</div>}
    </header>
  );
}

export function Panel({ title, actions, children, footer, pad = true, className }: {
  title?: ReactNode; actions?: ReactNode; children?: ReactNode; footer?: ReactNode; pad?: boolean; className?: string;
}) {
  return (
    <section className={cx('panel', className)}>
      {(title || actions) && <div className="panel-head">{typeof title === 'string' ? <h2>{title}</h2> : title}<div className="spacer" />{actions}</div>}
      <div className={pad ? 'panel-body' : undefined}>{children}</div>
      {footer && <div className="panel-foot">{footer}</div>}
    </section>
  );
}

export function Badge({ tone, children, dot, icon }: { tone?: 'success' | 'warning' | 'danger' | 'info' | 'ai' | 'human' | 'brand' | 'outline'; children: ReactNode; dot?: boolean; icon?: ReactNode }) {
  // Orange-on-peach tones use the design's exact accent colours (documented contrast exception, see docs/DESIGN.md).
  const accent = tone === 'brand' || tone === 'ai' || tone === 'warning';
  return <span className={cx('badge', tone && `badge-${tone}`)} data-accent-text={accent || undefined}>{dot && <span className="dot" />}{icon}{children}</span>;
}

const STATUS_TONES: Record<string, 'success' | 'warning' | 'danger' | 'info' | 'ai' | 'human' | 'outline' | undefined> = {
  connected: 'success', active: 'success', published: 'success', ready: 'success', paid: 'success', succeeded: 'success',
  sent: 'success', delivered: 'success', read: 'success', resolved: 'success', won: 'success', completed: 'success', online: 'success', trial: 'info', trialing: 'info',
  pending: 'warning', processing: 'info', running: 'info', testing: 'info', draft: undefined, queued: 'info', scheduled: 'info', open: 'info', new: 'info',
  requires_approval: 'warning', past_due: 'warning', paused: 'warning', away: 'warning', escalated: 'danger', reopened: 'warning',
  failed: 'danger', error: 'danger', suspended: 'danger', dead: 'danger', lost: 'danger', void: 'outline', uncollectible: 'danger', canceled: 'outline',
  not_connected: 'outline', disconnected: 'outline', unsupported: 'outline', archived: 'outline', closed: 'outline', offline: 'outline', spam: 'danger',
};
export function StatusBadge({ status, label }: { status: string; label?: string }) {
  return <Badge tone={STATUS_TONES[status]} dot>{label ?? status.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase())}</Badge>;
}

export function HandlerTag({ handler }: { handler: string }) {
  const label = { ai: 'AI agent', human: 'Human', flow: 'Flow', none: 'Unrouted' }[handler] ?? handler;
  return <span className={`handler handler-${handler}`}>{label}</span>;
}

export function Avatar({ name, src, size, presence }: { name?: string | null; src?: string | null; size?: 'sm' | 'lg'; presence?: string }) {
  return (
    <span className={cx('avatar', size && `avatar-${size}`)} aria-hidden>
      {src ? <img src={src} alt="" /> : initials(name)}
      {presence && <span className={`presence presence-${presence}`} />}
    </span>
  );
}

export function Stat({ label, value, delta, icon, hint }: { label: ReactNode; value: ReactNode; delta?: number | null; icon?: ReactNode; hint?: ReactNode }) {
  return (
    <div className="panel stat">
      <span className="stat-label">{icon}{label}</span>
      <span className="stat-value">{value}</span>
      {delta !== undefined && delta !== null && Number.isFinite(delta) ? (
        <span className={cx('stat-delta', delta >= 0 ? 'up' : 'down')}>{delta >= 0 ? '+' : ''}{(delta * 100).toFixed(1)}% vs previous period</span>
      ) : hint ? <span className="stat-delta faint">{hint}</span> : null}
    </div>
  );
}

export function Meter({ used, limit, label, name }: { used: number; limit: number | null | undefined; label?: ReactNode; name?: string }) {
  const unlimited = limit === null || limit === undefined || limit < 0;
  const p = unlimited ? 0 : Math.min(1, limit ? used / limit : 1);
  return (
    <div className="stack" style={{ gap: 6 }}>
      {label && <div className="row small"><span>{label}</span><span className="spacer" /><span className="num muted">{used.toLocaleString()} / {unlimited ? 'Unlimited' : limit!.toLocaleString()}</span></div>}
      <div className={cx('meter', p >= 1 ? 'over' : p >= 0.8 && 'warn')} role="meter" aria-label={name ?? (typeof label === 'string' ? label : 'Usage')} aria-valuenow={used} aria-valuemin={0} aria-valuemax={unlimited ? undefined : limit!}>
        <span style={{ width: `${unlimited ? 4 : p * 100}%` }} />
      </div>
    </div>
  );
}

/* ───── States ───── */
export function EmptyState({ icon, title, children, action }: { icon?: ReactNode; title: ReactNode; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="empty">
      {icon && <div className="empty-icon">{icon}</div>}
      <h3>{title}</h3>
      {children && <p>{children}</p>}
      {action}
    </div>
  );
}

export function Skeleton({ h = 16, w = '100%', r }: { h?: number; w?: number | string; r?: number }) {
  return <div className="skeleton" style={{ height: h, width: w, borderRadius: r }} aria-hidden />;
}

export function LoadingRows({ rows = 5 }: { rows?: number }) {
  return <div className="stack" style={{ padding: 20 }} aria-busy="true" aria-label="Loading">{Array.from({ length: rows }).map((_, i) => <Skeleton key={i} h={18} w={`${90 - i * 7}%`} />)}</div>;
}

export function Alert({ tone = 'info', title, children, action }: { tone?: 'info' | 'warning' | 'danger' | 'success' | 'ai'; title?: ReactNode; children?: ReactNode; action?: ReactNode }) {
  const Icon = { info: Info, warning: AlertTriangle, danger: AlertCircle, success: CheckCircle2, ai: Sparkles }[tone];
  return (
    <div className={`alert alert-${tone}`} role={tone === 'danger' ? 'alert' : 'status'}>
      <Icon aria-hidden />
      <div className="stack" style={{ gap: 2, flex: 1 }}>{title && <strong>{title}</strong>}{children && <div>{children}</div>}</div>
      {action}
    </div>
  );
}

export function ErrorState({ error, onRetry }: { error: ApiError | Error | null | undefined; onRetry?: () => void }) {
  if (!error) return null;
  return <Alert tone="danger" title="Couldn’t load this" action={onRetry && <Button size="sm" onClick={onRetry}>Retry</Button>}>{error.message}</Alert>;
}

/* ───── Tabs ───── */
export function Tabs<T extends string>({ value, onChange, tabs }: { value: T; onChange: (v: T) => void; tabs: { value: T; label: ReactNode; count?: number }[] }) {
  return (
    <div className="tabs" role="tablist">
      {tabs.map((t) => (
        <button key={t.value} role="tab" className="tab" aria-selected={value === t.value} onClick={() => onChange(t.value)}>
          {t.label}{t.count !== undefined && <span className="faint num"> {t.count}</span>}
        </button>
      ))}
    </div>
  );
}

export function Segmented<T extends string>({ value, onChange, options, label }: { value: T; onChange: (v: T) => void; options: { value: T; label: ReactNode }[]; label?: string }) {
  return (
    <div className="seg" role="group" aria-label={label}>
      {options.map((o) => <button key={o.value} aria-pressed={value === o.value} onClick={() => onChange(o.value)}>{o.label}</button>)}
    </div>
  );
}

/* ───── Modal / Drawer ───── */
function useEscape(onClose: () => void) {
  useEffect(() => {
    const f = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', f);
    return () => document.removeEventListener('keydown', f);
  }, [onClose]);
}

function useFocusTrap(ref: React.RefObject<HTMLElement>) {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const prev = document.activeElement as HTMLElement | null;
    const first = el.querySelector<HTMLElement>('input, select, textarea, button:not([data-close]), [tabindex]:not([tabindex="-1"])');
    (first ?? el).focus();
    return () => prev?.focus?.();
  }, [ref]);
}

export function Modal({ open, onClose, title, children, footer, size }: { open: boolean; onClose: () => void; title: ReactNode; children: ReactNode; footer?: ReactNode; size?: 'lg' }) {
  const ref = useRef<HTMLDivElement>(null);
  if (!open) return null;
  return createPortal(<ModalInner refEl={ref} onClose={onClose} title={title} footer={footer} size={size}>{children}</ModalInner>, document.body);
}
function ModalInner({ refEl, onClose, title, children, footer, size }: { refEl: React.RefObject<HTMLDivElement>; onClose: () => void; title: ReactNode; children: ReactNode; footer?: ReactNode; size?: 'lg' }) {
  useEscape(onClose);
  useFocusTrap(refEl);
  const labelId = useId();
  return (
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div ref={refEl} className={cx('modal', size === 'lg' && 'modal-lg')} role="dialog" aria-modal="true" aria-labelledby={labelId} tabIndex={-1}>
        <div className="modal-head"><h2 id={labelId}>{title}</h2><Button variant="ghost" iconOnly size="sm" data-close onClick={onClose} icon={<X />}>Close</Button></div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-foot">{footer}</div>}
      </div>
    </div>
  );
}

export function Drawer({ open, onClose, title, children, footer }: { open: boolean; onClose: () => void; title: ReactNode; children: ReactNode; footer?: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  if (!open) return null;
  return createPortal(<DrawerInner refEl={ref} onClose={onClose} title={title} footer={footer}>{children}</DrawerInner>, document.body);
}
function DrawerInner({ refEl, onClose, title, children, footer }: { refEl: React.RefObject<HTMLDivElement>; onClose: () => void; title: ReactNode; children: ReactNode; footer?: ReactNode }) {
  useEscape(onClose);
  useFocusTrap(refEl);
  const labelId = useId();
  return (
    <div className="overlay drawer-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div ref={refEl} className="drawer" role="dialog" aria-modal="true" aria-labelledby={labelId} tabIndex={-1}>
        <div className="modal-head"><h2 id={labelId}>{title}</h2><Button variant="ghost" iconOnly size="sm" data-close onClick={onClose} icon={<X />}>Close</Button></div>
        <div className="modal-body" style={{ flex: 1 }}>{children}</div>
        {footer && <div className="modal-foot">{footer}</div>}
      </div>
    </div>
  );
}

/* ───── Confirm ───── */
type ConfirmOpts = { title: string; body?: ReactNode; confirmLabel?: string; danger?: boolean };
const ConfirmCtx = createContext<(o: ConfirmOpts) => Promise<boolean>>(async () => false);
export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<(ConfirmOpts & { resolve: (v: boolean) => void }) | null>(null);
  const confirm = useCallback((o: ConfirmOpts) => new Promise<boolean>((resolve) => setState({ ...o, resolve })), []);
  const close = (v: boolean) => { state?.resolve(v); setState(null); };
  return (
    <ConfirmCtx.Provider value={confirm}>
      {children}
      <Modal open={!!state} onClose={() => close(false)} title={state?.title ?? ''}
        footer={<><Button onClick={() => close(false)}>Cancel</Button><Button variant={state?.danger ? 'danger' : 'primary'} onClick={() => close(true)}>{state?.confirmLabel ?? 'Confirm'}</Button></>}>
        {state?.body && <div className="muted">{state.body}</div>}
      </Modal>
    </ConfirmCtx.Provider>
  );
}
export const useConfirm = () => useContext(ConfirmCtx);

/* ───── Toasts ───── */
type Toast = { id: number; tone: 'success' | 'error' | 'info'; text: ReactNode };
const ToastCtx = createContext<(text: ReactNode, tone?: Toast['tone']) => void>(() => {});
export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<Toast[]>([]);
  const push = useCallback((text: ReactNode, tone: Toast['tone'] = 'success') => {
    const id = Date.now() + Math.random();
    setItems((x) => [...x, { id, tone, text }]);
    setTimeout(() => setItems((x) => x.filter((t) => t.id !== id)), tone === 'error' ? 7000 : 3800);
  }, []);
  return (
    <ToastCtx.Provider value={push}>
      {children}
      {createPortal(
        <div className="toasts" aria-live="polite">
          {items.map((t) => (
            <div key={t.id} className={cx('toast', t.tone)}>
              {t.tone === 'error' ? <AlertCircle /> : t.tone === 'success' ? <CheckCircle2 /> : <Info />}<span>{t.text}</span>
            </div>
          ))}
        </div>, document.body)}
    </ToastCtx.Provider>
  );
}
export const useToast = () => useContext(ToastCtx);

/* ───── Menu (dropdown) ───── */
export function Menu({ trigger, children, align = 'end', width }: { trigger: (p: { onClick: () => void; 'aria-expanded': boolean; 'aria-haspopup': 'menu' }) => ReactNode; children: ReactNode | ((close: () => void) => ReactNode); align?: 'start' | 'end'; width?: number }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const f = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    const k = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', f); document.addEventListener('keydown', k);
    return () => { document.removeEventListener('mousedown', f); document.removeEventListener('keydown', k); };
  }, [open]);
  const close = () => setOpen(false);
  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-flex' }}>
      {trigger({ onClick: () => setOpen((o) => !o), 'aria-expanded': open, 'aria-haspopup': 'menu' })}
      {open && (
        <div className="menu" role="menu" style={{ top: 'calc(100% + 6px)', [align === 'end' ? 'insetInlineEnd' : 'insetInlineStart']: 0, width } as any} onClick={(e) => { if ((e.target as HTMLElement).closest('.menu-item')) close(); }}>
          {typeof children === 'function' ? children(close) : children}
        </div>
      )}
    </div>
  );
}
export function MenuItem({ icon, children, onClick, danger, href }: { icon?: ReactNode; children: ReactNode; onClick?: () => void; danger?: boolean; href?: string }) {
  if (href) return <a role="menuitem" className={cx('menu-item', danger && 'danger')} href={href}>{icon}{children}</a>;
  return <button role="menuitem" className={cx('menu-item', danger && 'danger')} onClick={onClick}>{icon}{children}</button>;
}

/* ───── Misc ───── */
export function CopyButton({ text, label = 'Copy', size = 'sm' }: { text: string; label?: string; size?: 'sm' | 'md' }) {
  const [done, setDone] = useState(false);
  return (
    <Button size={size} variant="secondary" icon={done ? <Check /> : <Copy />}
      onClick={async () => { try { await navigator.clipboard.writeText(text); setDone(true); setTimeout(() => setDone(false), 1500); } catch { /* clipboard blocked */ } }}>
      {done ? 'Copied' : label}
    </Button>
  );
}

export function CodeBlock({ code }: { code: string }) {
  return <div className="code-block"><div className="copy"><CopyButton text={code} /></div>{code}</div>;
}

export function Pager({ page, pageSize, total, onPage }: { page: number; pageSize: number; total: number; onPage: (p: number) => void }) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  return (
    <div className="pager">
      <span className="num">{total === 0 ? 0 : (page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of {total.toLocaleString()}</span>
      <Button size="sm" disabled={page <= 1} onClick={() => onPage(page - 1)}>Previous</Button>
      <Button size="sm" disabled={page >= pages} onClick={() => onPage(page + 1)}>Next</Button>
    </div>
  );
}

export function FormError({ error }: { error?: ApiError | null }) {
  if (!error) return null;
  return <Alert tone="danger">{error.message}</Alert>;
}
